import { getNegotiationErrorMessage } from "@/util/negotiations";
import { eNegotiationErrorId, eNegotiationJourney } from "@/util/enums/negotiation";

const negotiation = {
    namespaced: true,
    state: {
        activeTabIndex: 0,
        negotiationSelected: {},
        offersTimeRemaining: {},
        isStillCalculating: [],
        startCountdown: false,
        negotiationsList: [],
        fuelPrices: [],
        showRenegotiateModal: false,
        showAcceptCounterOffer: false,
        acceptedCounterId: -1,
        showPurchaseConfirmed: false,
        purchaseStatusMessage: '',
        showNegotiationPlacedConfirmed: false,
        placeNegotiationStatusMessage: '',
        showStartNegotiation: false,
        showRenegotiationConfirmed: false,
        renegotiationStatusMessage: '',

        /* The following states are specific for sellers */
        negotiationSelectedId: '',
        fuelGroupSelected: '',
        negotiationListTimer: [],
        negotiationListActiveStatus: [],
        negotiationFuelGroups: [],
        showAcceptNegotiation: false,
        negotiationUnavailableWindowShown: false,
        showSaleConfirmed: false,
        saleStatusMessage: '',
        placedCounter: '',
        negotiationUpdateMessage: ''
    },
    getters: {
        showStartNegotiation(state){
            return state.showStartNegotiation;
        },
        getFuelPricesChanges(state){
            return state.fuelPrices
        },
        getNegotiationListChanges(state){
            return state.negotiationsList
        },
        /* The following getters are specific for sellers */
        getFuelGroupChanges(state){
            return state.fuelGroupSelected
        },
        getNegotiationListTimer(state){
            return state.negotiationListTimer
        },
        getNegotiationListActiveStatus(state){
            return state.negotiationListActiveStatus
        },
        wasMyCounterAccepted(state, getters, rootState){
            let statuses = []

            if (state.fuelGroupSelected && state.fuelGroupSelected.negotiationList.length > 0){
                // Run through each negotiation
                state.fuelGroupSelected.negotiationList.forEach((negotiation, index) => {

                    /* Do the following only if there is at least one counter offer, otherwise this must be false */
                    if (negotiation.counterOffers.length > 0){
                        // Look for a counter placed by user logged in
                        let myCounter = ''
                        myCounter = negotiation.counterOffers.find(counter => counter.user.account.id == rootState.login.userLoggedIn.id)
                        
                        if (myCounter){
                            /* Check counter status: accepted or not */
                            if (myCounter.status_id == 'COMPLETED'){
                                statuses.splice(index, 1, true)
                            }
                            else {
                                statuses.splice(index, 1, false)
                            }
                        }
                        else {
                            statuses.splice(index, 1, false)
                        }
                    }
                    else {
                        statuses.splice(index, 1, false)
                    }
                })
            }
            
            return statuses
        },
        hasPlacedCounter(state, getters, rootState){
            let placedCounter = ''
            
            if (state.activeTabIndex > 0 && getters.getFuelGroupChanges.negotiationList){
                if (getters.getFuelGroupChanges.negotiationList[state.activeTabIndex - 1].counterOffers && 
                    getters.getNegotiationListChanges){
                    // Update placedCounter info
                    placedCounter = getters.getFuelGroupChanges.negotiationList[state.activeTabIndex - 1].counterOffers.
                        find(counter => counter.user.account.id == rootState.login.userLoggedIn.id)
                }
    
                if (placedCounter){
                    state.placedCounter = placedCounter
                    return true
                }
                else {
                    state.placedCounter = ''
                    return false
                }
            }
        },
    },
    mutations:{
        SELECT_TAB:(state, index)=>{
            state.activeTabIndex = index
        },
        RESET_FUEL_GROUP_SELECTED:state=>{
            state.fuelGroupSelected = ''
        },
        SELECT_NEGOTIATION: (state, payload) => {
          state.negotiationSelected = payload.selectedNegotiation
          state.startCountdown = true
        },
        DESELECT_NEGOTIATION: (state) => {
            /* Let's reset everything and then display the selected negotiation */
            state.offersTimeRemaining = {}
            state.isStillCalculating = []
            
            state.negotiationSelected = ''
            
            state.startCountdown = false
        },
        CALCULATE_OFFER_COUNTDOWNS:state=>{
            if (state.negotiationSelected.counterOffers){
                state.negotiationSelected.counterOffers.forEach(offer => {
                    state.offersTimeRemaining[offer._id] = parseInt((new Date(offer.expires) - new Date())/1000)
                });
            }
        },
        STOP_COUNTDOWN:state=>{
            state.startCountdown = false
        },
        UPDATE_OFFER_VALIDITY:state=>{
            // Deem all counters as inactive once negotiation is completed
            for (let i = 0; i < state.negotiationSelected.counterOffers.length; i++){
                state.isStillCalculating.splice(i, i + 1, 
                    (state.negotiationSelected.status_id == 'ACTIVE' ? 
                    !(new Date() > new Date(state.negotiationSelected.counterOffers[i].expires)) : false))
            }
        },
        UPDATE_FUEL_PRICES: (state, payload) => {
            state.fuelPrices = payload
        },
        GET_NEGOTIATIONS: (state, { negotiations, accountDetails }) => {
            for (const negotiation of negotiations){
                const brands = accountDetails && accountDetails.brands;
                
                // Let's see if any of the seller's brand matches the completed negotiation's
                if (negotiation.brand && brands && brands.length > 0){
                    const negotiationBrand = brands.find(brand => brand._id === negotiation.brand.brand_id);
                    negotiation.brand = negotiationBrand;
                }

                // Let's see if any of the seller's brand matches any of the counter offers'
                for (const counter of negotiation.counterOffers){
                    if (counter.brand && brands && brands.length > 0){
                        const counterBrand = brands.find(brand => brand._id === counter.brand.brand_id);
                        counter.brand = counterBrand;
                    }
                }
            }
            
            state.negotiationsList = negotiations;
        },
        UPDATE_NEGOTIATIONS_FUEL_GROUP: (state, fuelsList) => {
            state.negotiationFuelGroups = []

            /* Group the negotiations based on their fuel types */
            fuelsList.forEach((fuel, index) => {
                if (fuel.type == 'SPOT' || fuel.type == 'BUNKERING'){
                    state.negotiationFuelGroups.splice(index, 1, {fuel: fuel, negotiationList: [], lastActivity: ''})
                }
            })

            if (state.negotiationsList){
                state.negotiationsList.forEach(async (negotiation, index) => {
                    let group = await state.negotiationFuelGroups.find(fuelGroup => fuelGroup.fuel._id == negotiation.fuel_id)
                    if (group){
                        group.negotiationList.splice(group.negotiationList.length, 1, negotiation)
                    }
                })
            }
        },
        SORT_NEGOTIATIONS_FUEL_GROUP: (state) => {
            /* Sort fuel group list based on most recent activity */
            state.negotiationFuelGroups.forEach(group => {
                const activityTimestampList = group.negotiationList.map(negotiation => new Date(negotiation.lastActivity))
                if (activityTimestampList.length > 0){
                    group.lastActivity = Math.max(...activityTimestampList)
                }
                else {
                    group.lastActivity = -1
                }
            })

            state.negotiationFuelGroups.sort((a, b) => {
                return b.lastActivity - a.lastActivity
            })
        },
        UPDATE_REVISED_NEGOTIATION: (state, payload) => {
            state.negotiationSelected = payload.selectedNegotiation
        },
        UPDATE_SHOW_RENEGOTIATE_MODAL: (state, payload) => {
            state.showRenegotiateModal = payload
        },
        UPDATE_SHOW_ACCEPT_COUNTER_OFFER: (state, payload) => {
            state.showAcceptCounterOffer = payload
        },
        SET_COUNTER_TO_ACCEPT_INDEX: (state, payload) => {
            state.acceptedCounterId = payload
        },
        UPDATE_SHOW_PURCHASE_CONFIRMED:(state, payload) => {
            if (payload.message){
                state.showPurchaseConfirmed = true
                state.purchaseStatusMessage = payload.message
            }
            else {
                state.showPurchaseConfirmed = false
                state.purchaseStatusMessage = ''
            }
        },
        UPDATE_SHOW_NEGOTIATION_PLACED_CONFIRMATION: (state, payload) => {
            if (payload.message){
                state.showNegotiationPlacedConfirmed = true

                if (payload.success){
                    state.placeNegotiationStatusMessage = 'Negotiation submitted to market'
                }
                else {
                    state.placeNegotiationStatusMessage = payload.message
                }
            }
            else {
                state.showNegotiationPlacedConfirmed = false
                state.placeNegotiationStatusMessage = ''
            }
        },
        UPDATE_SHOW_RENEGOTIATION_CONFIRMED: (state, payload) => {
            if (payload.message){
                state.showRenegotiationConfirmed = true

                if (payload.success){
                    state.renegotiationStatusMessage = 'Negotiation has been successfully revised'
                }
                else {
                    state.renegotiationStatusMessage = payload.message
                }
            }
            else {
                state.showRenegotiationConfirmed = false
                state.renegotiationStatusMessage = ''
            }
        },

        /* The following mutations are specific for sellers */

        SET_NEGOTIATION_SELECTED_ID: (state, payload) => {
            state.negotiationSelectedId = payload
        },
        CLEAR_PLACED_COUNTER: (state) => {
            state.placedCounter = ''
        },
        SELECT_FUEL_GROUP: (state, payload) => {
            const selectedGroup = state.negotiationFuelGroups.find(fuelGroup => fuelGroup.fuel._id == payload)
            state.fuelGroupSelected = selectedGroup
        },
        UPDATE_NEGOTIATION_LIST_TIMER: (state, payload) => {
            state.negotiationListTimer.splice(payload.index, payload.index + 1, payload.time)
        },
        UPDATE_NEGOTIATION_LIST_ACTIVE_STATUS: (state, payload) => {
            if (payload.time > 0 && state.fuelGroupSelected.negotiationList[payload.index].status_id == 'ACTIVE'){
                state.negotiationListActiveStatus.splice(payload.index, payload.index + 1, true)
            }
            else {
                state.negotiationListActiveStatus.splice(payload.index, payload.index + 1, false)
            }
        },
        UPDATE_SHOW_START_NEGOTIATION:(state, payload) => {
            state.showStartNegotiation = payload
        },
        UPDATE_SHOW_ACCEPT_NEGOTIATION:(state, payload) => {
            state.showAcceptNegotiation = payload
        },
        SHOW_NEGOTIATION_UNAVAILABLE_WINDOW: (state, payload) => {
            state.negotiationUnavailableWindowShown = payload
        },
        UPDATE_SHOW_SALE_CONFIRMED:(state, payload) => {
            if (payload.success){
                state.showSaleConfirmed = true
                state.saleStatusMessage = 'Fuel Sold!'
            }
            else {
                state.showSaleConfirmed = false
                state.saleStatusMessage = 'Sorry, we are currently unable to place your order'
            }
        },
        UPDATE_NEGOTIATIONS_FUEL_GROUP_COUNTERS:(state, negotiationId) => {
            /* Update negotiation details in selected fuel group with the newly placed counter */
            const updatedNegotiation = state.negotiationsList.find(negotiation => negotiation._id == negotiationId)
            const indexToChange = state.fuelGroupSelected.negotiationList.findIndex(negotiation => negotiation._id == negotiationId)
            state.fuelGroupSelected.negotiationList.splice(indexToChange, 1, updatedNegotiation)
        },
        SET_NEGOTIATION_UPDATE_MESSAGE: (state, message) => {
            state.negotiationUpdateMessage = message
        }
    },
    actions:{
        selectNegotiation:({commit, rootState}, negotiationId)=>{
            rootState.axios.get(`/api/negotiation/${negotiationId}`).then((res) => {
              if (res.data.success) {
                const payload = {
                  selectedNegotiation: res.data.negotiation,
                };
                commit("SELECT_NEGOTIATION", payload);
              } else {
                console.log("Error found", res);
              }
            });
        },
        deselectNegotiation: ({commit}) => {
            commit('DESELECT_NEGOTIATION')
        },
        placeNegotiation: ({dispatch, rootState}, response) => {
            const payload = {
                axios: rootState.axios,
                negotiation: response
            }
            
            const negotiationsPayload = {
                status: 'All',
                accountId: rootState.login.userLoggedIn.id
            }

            payload.axios.post("/api/negotiation/place", payload.negotiation).then((response) => {
              if (response.data.success) {
                dispatch("getNegotiations", negotiationsPayload);
              }
              dispatch("updateShowNegotiationPlacedConfirmation", response.data);
              return true;
            });
        },
        updateShowNegotiationPlacedConfirmation: ({commit}, payload) => {
            commit('UPDATE_SHOW_NEGOTIATION_PLACED_CONFIRMATION', payload)
        },
        reviseActiveNegotiation: ({commit, dispatch, rootState}, payload) => {
            const negotiationsPayload = {
                status: 'All',
                accountId: rootState.login.userLoggedIn.id
            }

            const { negotiation_id } = payload;
            rootState.axios.put(`api/negotiation/${negotiation_id}/revise`, payload).then((res) => {
              if (res.data.success) {
                rootState.axios.get(`/api/negotiation/${negotiation_id}`).then((res) => {
                  if (res.data.success) {
                    const payload = {
                      selectedNegotiation: res.data.negotiation,
                    };
                    commit("UPDATE_REVISED_NEGOTIATION", payload);
                    dispatch("getNegotiations", negotiationsPayload);
                  } else {
                    console.log("Error found", res);
                  }
                });

                dispatch("updateShowRenegotiationConfirmed", res.data);
              }
            });
        },
        updateShowRenegotiationConfirmed: ({commit}, payload) => {
            commit('UPDATE_SHOW_RENEGOTIATION_CONFIRMED', payload)
        },
        updateFuelPrices: ({commit}, payload) => {
            commit('UPDATE_FUEL_PRICES', payload)
        },
        getNegotiations: async ({commit, dispatch, rootState}, payload) => {
            const response = await rootState.axios.get("/api/negotiations", {
              params: {
                status: payload.status,
              },
            });
            
            if (response.data.success){
                const getNegotiationsPayload = {
                    negotiations: response.data.negotiations,
                    accountDetails: rootState.accounts.accountDetails
                };
                commit('GET_NEGOTIATIONS', getNegotiationsPayload);
            }
            
            /* Group negotiations according to their fuel types for seller */
            if (rootState.login.userLoggedIn.category == 'seller'){
                dispatch('updateNegotiationsFuelGroup').then(() => {

                    dispatch('sortNegotiationsFuelGroup');
                    if( rootState.negotiation.fuelGroupSelected !== '' )
                        dispatch('selectFuelGroup', rootState.negotiation.fuelGroupSelected.fuel._id);
                })
            }
        },
        updateNegotiationsFuelGroup: ({commit, rootState}) => {
            commit('UPDATE_NEGOTIATIONS_FUEL_GROUP', rootState.fuelsType.fuels)
        },
        sortNegotiationsFuelGroup: ({commit}) => {
            commit('SORT_NEGOTIATIONS_FUEL_GROUP')
        },
        updateShowRenegotiateModal: ({commit}, payload) => {
            commit('UPDATE_SHOW_RENEGOTIATE_MODAL', payload)
        },
        updateShowAcceptCounterOffer: ({commit}, payload) => {
            commit('UPDATE_SHOW_ACCEPT_COUNTER_OFFER', payload)
        },
        setAcceptedCounterId: ({commit}, payload) => {
            commit('SET_COUNTER_TO_ACCEPT_INDEX', payload)
        },
        updateShowPurchaseConfirmed: ({commit}, payload) => {
            commit('UPDATE_SHOW_PURCHASE_CONFIRMED', payload)
        },
        acceptCounterOfferConfirmed: async ({commit, dispatch, rootState}, payload) => {
            const negotiationsPayload = {
                status: 'All',
                accountId: rootState.login.userLoggedIn.id
            }

            try {
              const orderResponse = await rootState.axios.get(`/api/negotiation/counter/${payload.counter_id}/accept`,
                {
                    params: {
                      negotiationId: payload.negotiation_id,
                      marketWindowId: rootState.orders.marketWindowId,
                      orderReference: payload.orderReference,
                    },
                }
              );

              if (orderResponse.data.success) {
                dispatch("orders/allowUpdateOrderList", true, { root: true });
                /* Now negotiation selected details needs to be updated with the new status */
                dispatch("getNegotiations", negotiationsPayload).then(() => {
                  rootState.axios
                    .get(`/api/negotiation/${payload.negotiation_id}`)
                    .then((res) => {
                      if (res.data.success) {
                        const updatedPayload = {
                          selectedNegotiation: res.data.negotiation,
                        };
                        // Using mutation, update negotiationSelected details
                        commit("UPDATE_REVISED_NEGOTIATION", updatedPayload);
                      } else {
                        console.log("Error found", res);
                      }
                    })
                    .then(async () => {
                      // Display confirmation message
                      dispatch("updateShowPurchaseConfirmed", orderResponse.data);

                      try {
                        const buyerUsers = await rootState.axios.get("/api/getUsersByAccount", {
                          params: { accountId: rootState.login.userLoggedIn.id },
                        });

                        const notificationPayload = {
                          type_id: "ORDER",
                          participants: buyerUsers.data.users,
                          group_id: orderResponse.data.order._id,
                          content: "",
                        };
                        dispatch("notifications/sendNotification", notificationPayload, {
                          root: true,
                        }).then(() => {
                          dispatch("notifications/getNotifications", null, { root: true });
                        });
                      } catch (ex) {
                        console.dir(ex);
                        res.status(500).json({ message: "Server Error for POST Notification" });
                      }
                    });
                });
              }
            } catch (ex) {
              const errorData = ex.response && ex.response.data && ex.response.data.body;
              const errorMessage = getNegotiationErrorMessage(errorData, eNegotiationJourney.Accept);
              commit("SET_NEGOTIATION_UPDATE_MESSAGE", errorMessage);
              dispatch("updateShowSaleConfirmed", false);
              dispatch("updateShowAcceptCounterOffer", false);
              dispatch("showNegotiationUnavailableWindow", true);
            }
        },

        /* The following actions are specific for sellers */
        
        setNegotiationSelectedId: ({commit}, payload) => {
            commit('SET_NEGOTIATION_SELECTED_ID', payload)
        },
        clearPlacedCounter: ({commit}) => {
            commit('CLEAR_PLACED_COUNTER')
        },
        selectFuelGroup: ({commit}, payload) => {
            commit('SELECT_FUEL_GROUP', payload)
        },
        updateNegotiationListTimer: ({commit}, payload) => {
            commit('UPDATE_NEGOTIATION_LIST_TIMER', payload)
        },
        updateNegotiationListActiveStatus: ({commit}, payload) => {
            commit('UPDATE_NEGOTIATION_LIST_ACTIVE_STATUS', payload)
        },
        addCounterOffer: ({commit, dispatch, rootState}, counterInfo) => {
            const payload = {
                counterOffer: counterInfo.counter,
                negotiation_id: counterInfo.negotiation_id
            }

            const negotiationsPayload = {
                status: 'All',
                accountId: rootState.login.userLoggedIn.id
            }

            rootState.axios
              .post("/api/negotiation/counter/place", payload)
              .then((res) => {
                dispatch("getNegotiations", negotiationsPayload).then(() => {
                  commit("UPDATE_NEGOTIATIONS_FUEL_GROUP_COUNTERS", payload.negotiation_id);
                });
              })
              .catch((ex) => {
                const errorData = ex.response && ex.response.data && ex.response.data.body;
                const errorMessage = getNegotiationErrorMessage(errorData, eNegotiationJourney.Counter);
                commit("SET_NEGOTIATION_UPDATE_MESSAGE", errorMessage);
                dispatch("showNegotiationUnavailableWindow", true);
              });
        },
        editCounterOffer: ({commit, dispatch, rootState}, counterInfo) => {
            const payload = {
                counterOffer: counterInfo.counter,
                negotiation_id: counterInfo.negotiation_id,
            };

            const negotiationsPayload = {
                status: 'All',
                accountId: rootState.login.userLoggedIn.id
            }

            rootState.axios
              .put(`/api/negotiation/counter/${counterInfo.counterOfferId}/edit`, payload)
              .then((res) => {
                dispatch("getNegotiations", negotiationsPayload).then(() => {
                  commit("UPDATE_NEGOTIATIONS_FUEL_GROUP_COUNTERS", payload.negotiation_id);
                });
              })
              .catch((ex) => {
                const errorData = ex.response && ex.response.data && ex.response.data.body;
                const errorMessage = getNegotiationErrorMessage(errorData, eNegotiationJourney.Counter);
                commit("SET_NEGOTIATION_UPDATE_MESSAGE", errorMessage);
                dispatch("showNegotiationUnavailableWindow", true);
              });
        },
        updateShowStartNegotiation: ({commit}, payload) => {
            commit('UPDATE_SHOW_START_NEGOTIATION', payload)
        },
        updateShowAcceptNegotiation: ({commit}, payload) => {
            commit('UPDATE_SHOW_ACCEPT_NEGOTIATION', payload)
        },
        checkNegotiationAvailability: async ({commit, rootState}, payload) => {
            const { negotiationId } = payload;
            const response = await rootState.axios.get(`/api/negotiation/${negotiationId}`);
            let messageToDisplay = ''

            if (response.data.success){
                if (response.data.negotiation.status_id == 'ACTIVE'){
                    if (payload.isPlacingCounter && response.data.negotiation.counterOffers.find(counter => counter.user.account.id == rootState.login.userLoggedIn.id)){
                        messageToDisplay = 'A counter offer from this account has been placed. Please refresh your page'
                        commit('SET_NEGOTIATION_UPDATE_MESSAGE', messageToDisplay)
                        return false
                    }
                    else {
                        return true
                    }
                }
            }

            messageToDisplay = 'Sorry, negotiation has either expired, been accepted or withdrawn. Please refresh your page'
            commit('SET_NEGOTIATION_UPDATE_MESSAGE', messageToDisplay)
            return false
        },
        showNegotiationUnavailableWindow: ({commit}, payload) => {
            commit('SHOW_NEGOTIATION_UNAVAILABLE_WINDOW', payload)
        },
        acceptNegotiationConfirmed: async ({commit, dispatch, rootState}, payload) => {
            const negotiationsPayload = {
                status: 'All',
                accountId: rootState.login.userLoggedIn.id
            }

            try {
              const orderParams = {
                marketWindowId: rootState.orders.marketWindowId,
              }
              if (payload.brandId) {
                orderParams.brandId = payload.brandId;
              }
              const orderResponse = await rootState.axios.get(`/api/negotiation/${payload.negotiation_id}/accept`, { params: orderParams });

              if (orderResponse.data.success) {
                dispatch("orders/allowUpdateOrderList", true, { root: true });
                /* Update negotiation selected details with its new status */
                dispatch("getNegotiations", negotiationsPayload).then(() => {
                  commit("UPDATE_NEGOTIATIONS_FUEL_GROUP_COUNTERS", payload.negotiation_id);
                  // Display confirmation message
                  dispatch("updateShowSaleConfirmed", orderResponse.data);
                });

                try {
                  const sellerUsers = await rootState.axios.get("/api/getUsersByAccount", {
                    params: { accountId: rootState.login.userLoggedIn.id },
                  });

                  const notificationPayload = {
                    type_id: "ORDER",
                    participants: sellerUsers.data.users,
                    group_id: orderResponse.data.order._id,
                    content: "",
                  };
                  dispatch("notifications/sendNotification", notificationPayload, { root: true }).then(
                    () => {
                      dispatch("notifications/getNotifications", null, { root: true });
                    }
                  );
                } catch (ex) {
                  console.dir(ex);
                  res.status(500).json({ message: "Server Error for POST Notification" });
                }
              }
            } catch (ex) {
              const errorData = ex.response && ex.response.data && ex.response.data.body;
              const errorMessage = getNegotiationErrorMessage(errorData, eNegotiationJourney.Accept);
              commit("SET_NEGOTIATION_UPDATE_MESSAGE", errorMessage);
              dispatch("showNegotiationUnavailableWindow", true);
              dispatch("updateShowSaleConfirmed", false);
              dispatch("updateShowAcceptNegotiation", false);
            }
        },
        updateShowSaleConfirmed: ({commit}, payload) => {
            commit('UPDATE_SHOW_SALE_CONFIRMED', payload)
        }
    }
}

export default negotiation